import { useAppState } from "@/modules/app/useAppContext";
import { getTenant, isCodeQuery, isFreeStack, isMegaTenant, showWorkflow } from "@/helpers/tenant";
import { isAdmin, isOwner } from "@/services/auth";
import { PermissionsEnum } from "@/helpers/constants";
import {
  IconRocket,
  IconServer,
  IconSettings,
  IconCompass,
  IconCode,
  IconReportAnalytics,
  IconStack2,
  IconChartPie2,
  IconShield,
  IconDatabase,
  IconSparkles,
  IconBrain,
  IconUsers,
} from "@tabler/icons-react";
import { useMemo } from "react";
import { SidebarMenu } from "@altimateai/lego";
import { SidebarCollapsibleItem } from "./SidebarCollapsibleItem";
import { SidebarSubLink } from "./SidebarSubLink";
import { SidebarLink } from "./SidebarLink";
import { FreeUserSidebar } from "./FreeUserSidebar";

export const SidebarNav = () => {
  const tenant = getTenant();
  const {
    rbacEnabled,
    enableBiFeature,
    dbtWorkloadEnabled,
    costAttributionEnabled,
    enableCustomReports,
    enableSnowflakeContracts,
    enableComputePool,
    datastores,
    enableCostReports,
    enableCreditsPage,
    loggedinUser: user,
    isDemoTenant,
    ssoGroupSyncEnabled,
    isDatabricksTenant,
  } = useAppState();
  const isFreeUser = isFreeStack();
  const showUserGroupsLink = rbacEnabled;
  const showUsersPage = rbacEnabled ? isAdmin() || isOwner() : true;
  const showCredits = isFreeUser || enableCreditsPage || false;

  const is_organization_admin = useMemo(() => {
    return datastores?.some(ds => ds.is_organization_admin);
  }, [datastores]);

  if (isDemoTenant) {
    return (
      <SidebarMenu>
        <SidebarLink to="/studio" label="Studio" icon={<IconRocket />} />
        <SidebarLink to="/settings/datastores" label="Connections" icon={<IconSettings />} />
      </SidebarMenu>
    );
  }

  if (!tenant) {
    return (
      <SidebarMenu>
        <SidebarLink to="/settings/companies" label="Companies" icon={<IconSettings />} />
        <SidebarLink to="/rbac" label="Access Control Details" icon={<IconShield />} />
        <SidebarLink to="/langfuse" label="Langfuse Queries Audit" icon={<IconDatabase />} />
        <SidebarLink to="/ade-bench" label="ADE-Bench Results" icon={<IconReportAnalytics />} />
      </SidebarMenu>
    );
  }

  if (isFreeUser) {
    return <FreeUserSidebar />;
  }

  return (
    <SidebarMenu>
      <SidebarCollapsibleItem
        label="Summary"
        to="/summary"
        icon={<IconReportAnalytics />}
        activePaths={[
          "/summary",
          "/team-spends",
          "/accounts-cost",
          "/contracts",
          "/cost-breakdown",
        ]}
      >
        {isDatabricksTenant && <SidebarSubLink to="/cost-breakdown" label="Breakdown" />}
        {!isDatabricksTenant && costAttributionEnabled && (
          <SidebarSubLink to="/team-spends" label="Team Spends" />
        )}
        {!isDatabricksTenant && (isOwner() || isAdmin()) && is_organization_admin && (
          <SidebarSubLink to="/accounts-cost" label="Accounts Cost" />
        )}
        {!isDatabricksTenant && isOwner() && enableSnowflakeContracts && is_organization_admin && (
          <SidebarSubLink to="/contracts" label="Contracts" />
        )}
      </SidebarCollapsibleItem>
      <SidebarLink to="/discover" query="status=active" label="Discover" icon={<IconCompass />} />
      {!isDatabricksTenant && (
        <SidebarCollapsibleItem
          label="Datasets"
          to="/datasets"
          icon={<IconChartPie2 />}
          activePaths={["/datasets", "/tables"]}
        >
          <SidebarSubLink to="/tables" label="Usage" />
        </SidebarCollapsibleItem>
      )}
      {isDatabricksTenant ? (
        <SidebarCollapsibleItem
          label="Code"
          icon={<IconCode />}
          activePaths={["/jobs", "/queries"]}
        >
          <SidebarSubLink to="/jobs" label="Jobs" />
          <SidebarSubLink to="/queries" label="Queries" />
        </SidebarCollapsibleItem>
      ) : (
        isCodeQuery() &&
        (dbtWorkloadEnabled ? (
          <SidebarCollapsibleItem
            label="Code"
            icon={<IconCode />}
            activePaths={["/query", "/dbt_models"]}
          >
            <SidebarSubLink to="/query/all" label="Workloads" />
            <SidebarSubLink to="/dbt_models" label="dbt" />
          </SidebarCollapsibleItem>
        ) : (
          <SidebarLink to="/query/all" label="Code" icon={<IconCode />} />
        ))
      )}
      <SidebarCollapsibleItem
        label="Infra"
        to={isDatabricksTenant ? "/clusters" : "/warehouse"}
        icon={<IconServer />}
        activePaths={
          isDatabricksTenant
            ? ["/clusters", "/sql-warehouses"]
            : ["/warehouse", "/bi-reports", "/compute-pools"]
        }
      >
        {isDatabricksTenant ? (
          <>
            <SidebarSubLink to="/clusters" label="Clusters" />
            <SidebarSubLink to="/sql-warehouses" label="SQL Warehouses" />
          </>
        ) : (
          <>
            {enableBiFeature && <SidebarSubLink to="/bi-reports/dashboards" label="Insights" />}
            {enableComputePool && <SidebarSubLink to="/compute-pools" label="Compute Pools" />}
          </>
        )}
      </SidebarCollapsibleItem>
      <SidebarLink
        to="/users"
        label={isDatabricksTenant ? "Users" : "Users & Roles"}
        icon={<IconUsers />}
      />
      {isDatabricksTenant && <SidebarLink to="/ai-ml" label="AI/ML" icon={<IconBrain />} />}
      {enableCustomReports && (
        <SidebarLink to="/spaces" label="Spaces" icon={<IconStack2 />} isBeta />
      )}
      <SidebarLink to="/studio" label="Studio" icon={<IconSparkles />} isBeta />
      <SidebarCollapsibleItem
        label="Datamates"
        to="/datamates"
        icon={<IconRocket />}
        activePaths={["/datamates", "/knowledge-hub", "/workflow"]}
      >
        <SidebarSubLink to="/knowledge-hub" label="Knowledge hub" />
        <SidebarSubLink to="/datamates/memories" label="Memory hub" isBeta />
        {showWorkflow() && <SidebarSubLink to="/workflow" label="Workflow" isBeta />}
      </SidebarCollapsibleItem>
      <SidebarCollapsibleItem
        label="Settings"
        icon={<IconSettings />}
        activePaths={["/settings", "/usergroups", "/teammates"]}
      >
        {isOwner() && <SidebarSubLink to="/settings/datastores" label="Connections" />}
        {showUsersPage && <SidebarSubLink to="/settings/users" label="Users" />}
        {showUserGroupsLink && <SidebarSubLink to="/usergroups" label="Teams" />}
        {showCredits && <SidebarSubLink to="/settings/credits" label="Credits" />}
        <SidebarSubLink to="/settings/api-key" label="API Key" />
        {(isFreeUser || dbtWorkloadEnabled) && (
          <SidebarSubLink to="/settings/integrations" label="Integrations" />
        )}
        <SidebarSubLink to="/settings/notification" label="Notifications" />
        <SidebarSubLink to="/teammates" label="Enable Coaching" />
        <SidebarSubLink to="/settings/tags" label="Tags" />
        {isOwner() && user?.permission?.includes(PermissionsEnum.SECURITY_VIEW) && !isFreeUser && (
          <SidebarSubLink to="/settings/security" label="Security" />
        )}
        {ssoGroupSyncEnabled && isOwner() && (
          <SidebarSubLink to="/settings/sso-group-sync" label="SSO Group Sync" />
        )}
        {ssoGroupSyncEnabled && isOwner() && (
          <SidebarSubLink to="/settings/audit" label="Audit Log" />
        )}
        {costAttributionEnabled && (
          <SidebarSubLink to="/settings/cost-attribution" label="Cost Attribution" />
        )}
        {enableCostReports && <SidebarSubLink to="/subscriptions" label="Subscriptions" />}
        {isMegaTenant() && <SidebarSubLink to="/referrals" label="Referral Codes" />}
      </SidebarCollapsibleItem>
    </SidebarMenu>
  );
};
